import { useCallback, useState } from "react";

import {
  Box,
  FormField,
  TextInput,
  RadioGroup,
  Radio,
  ClipboardButton,
  Text,
  Textarea,
  Paragraph,
} from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useDropzone } from "react-dropzone";

import { useCreateGoogleServiceAccountMutation } from "../../graphql";
import { PermissionedButton } from "../permission";
import { DocsLink } from "src/components/docs-link";

export const gcpConfigValidator = (config): boolean => {
  if (config.type === "external_account") {
    return Boolean(config.audience && config.subject_token_type);
  }
  return Boolean(config.client_email && config.private_key);
};

/**
 * GCP Form
 * @param config
 * @param setConfig
 * @constructor
 */
export const GCPForm = ({ config, setConfig }) => {
  const [accessType, setAccessType] = useState<string>("managedServiceAccount");
  const { gcpWorkloadIdentityFederation: wifEnabled } = useFlags();

  const getAccessTypeForm = () => {
    if (accessType === "managedServiceAccount") {
      return (
        <ManagedServiceAccountSetup config={config} setConfig={setConfig} />
      );
    } else if (accessType === "customServiceAccount") {
      return (
        <CustomServiceAccountSetup config={config} setConfig={setConfig} />
      );
    } else if (accessType === "workloadIdentityFederation") {
      return <WifSetup config={config} setConfig={setConfig} />;
    } else {
      return null;
    }
  };

  return (
    <>
      <FormField label="Access method" mt={6}>
        <RadioGroup
          orientation="vertical"
          value={accessType}
          onChange={(type) => {
            setAccessType(type as string);
            // Clear out the other config when we switch.
            setConfig({});
          }}
        >
          <Radio
            label="Service account managed by Hightouch (recommended)"
            value="managedServiceAccount"
          />
          <Radio
            label="Bring your own service account"
            value="customServiceAccount"
          />
          {wifEnabled && (
            <Radio
              label="Workload Identity Federation"
              value="workloadIdentityFederation"
            />
          )}
        </RadioGroup>
      </FormField>

      {getAccessTypeForm()}
    </>
  );
};

const ManagedServiceAccountSetup = ({ config, setConfig }) => {
  const {
    isLoading: creatingServiceAccount,
    error: serviceAccountError,
    mutateAsync: createServiceAccount,
  } = useCreateGoogleServiceAccountMutation();
  const serviceAccountErrorMessage = parseError(serviceAccountError);

  return (
    <>
      {!config.client_email ? (
        <FormField
          description="Hightouch will provision a new GCP service account for you."
          error={serviceAccountErrorMessage}
          label="Service account details"
          mt={6}
        >
          <PermissionedButton
            permission={{
              v2: { resource: "workspace", grant: "can_update" },
            }}
            isLoading={creatingServiceAccount}
            onClick={async () => {
              const { createGoogleServiceAccount } = await createServiceAccount(
                {},
              );
              const credentialsStr =
                createGoogleServiceAccount?.serviceAccount?.credentials;
              const credentials = JSON.parse(credentialsStr);
              setConfig(credentials);
            }}
          >
            Create a new service account
          </PermissionedButton>
        </FormField>
      ) : (
        <FormField
          description="Please copy the service account address below. You will need it later when granting Hightouch access to your data. (You will bind IAM policies to this service account.)"
          label="Service account details"
          mt={6}
        >
          <Box display="flex" gap={3}>
            <TextInput isReadOnly value={config.client_email} />
            <ClipboardButton text={config.client_email} />
          </Box>
        </FormField>
      )}
    </>
  );
};

const parseError = (graphqlError: any) => {
  if (graphqlError) {
    const index = graphqlError.message.indexOf("{");
    const json = JSON.parse(graphqlError.message.slice(index));
    const error = json?.errors?.[0]?.message;

    return error;
  }
};

// keys required in private key upload
const requiredKeys = ["client_email", "private_key", "token_uri"];

const CustomServiceAccountSetup = ({ config, setConfig }) => {
  const [error, setError] = useState<string>("");

  const parseCredentials = (credentials) => {
    const missingKeys: string[] = [];
    for (const key of requiredKeys) {
      if (!credentials[key]) {
        missingKeys.push(key);
      }
    }
    if (missingKeys.length > 0) {
      setError(`Missing keys from credential file: ${missingKeys.join(", ")}`);
      return;
    }
    setConfig(credentials);
  };

  // Create file upload drop zone.
  const { getRootProps, getInputProps } = useDropzone({
    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    onDrop: useCallback(async (acceptedFiles) => {
      setError("");
      const file = acceptedFiles[0];
      if (!file) {
        return;
      }

      const fileContents = await file.text();
      try {
        parseCredentials(JSON.parse(fileContents));
      } catch (_err) {
        setError("Could not parse private key file.");
      }
    }, []),
    accept: ".json",
  });

  return (
    <>
      <FormField
        description={
          <>
            <Paragraph>
              This can be obtained in the Google Cloud web console by navigating
              to the <Text fontWeight="semibold">IAM</Text> page and clicking on{" "}
              <Text fontWeight="semibold">Service Accounts</Text> in the left
              sidebar. Then, find your service account in the list, go to its{" "}
              <Text fontWeight="semibold">Keys</Text> tab, and click{" "}
              <Text fontWeight="semibold">Add Key</Text>. Finally, click on{" "}
              <Text fontWeight="semibold">Create new key</Text> and choose{" "}
              <Text fontWeight="semibold">JSON</Text>.
            </Paragraph>
          </>
        }
        error={error}
        label="Upload JSON key file for service account"
        mt={6}
      >
        {config.client_id ? (
          <Box display="flex" flexDirection="column" gap={6}>
            <FormField label="Client ID">
              <TextInput isReadOnly value={config.client_id} />
            </FormField>

            <FormField label="Client email">
              <TextInput isReadOnly value={config.client_email} />
            </FormField>

            <FormField label="Private key">
              <Textarea isReadOnly value="<REDACTED>" />
            </FormField>
          </Box>
        ) : (
          <Box
            bg="base.lightBackground"
            borderWidth="hairline"
            borderColor="base.border"
            borderRadius="md"
            borderStyle="dashed"
          >
            <Box
              cursor="pointer"
              py={10}
              textAlign="center"
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              <Paragraph>
                Drag your file here, or click to select a file.
              </Paragraph>
            </Box>
          </Box>
        )}
      </FormField>
    </>
  );
};

const wifRequiredKeys = ["type", "audience", "subject_token_type"];

/**
 * Extract the impersonated service account email from the
 * service_account_impersonation_url field if present.
 * Format: https://iamcredentials.googleapis.com/v1/projects/-/serviceAccounts/EMAIL:generateAccessToken
 */
const extractSaEmail = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  const match = url.match(/serviceAccounts\/([^:]+)/);
  return match?.[1];
};

const WifSetup = ({ config, setConfig }) => {
  const [error, setError] = useState<string>("");

  const parseCredentials = (credentials) => {
    if (credentials.type !== "external_account") {
      setError(
        'Invalid credential configuration: "type" must be "external_account".',
      );
      return;
    }
    const missingKeys: string[] = [];
    for (const key of wifRequiredKeys) {
      if (!credentials[key]) {
        missingKeys.push(key);
      }
    }
    if (missingKeys.length > 0) {
      setError(
        `Missing keys from credential configuration: ${missingKeys.join(", ")}`,
      );
      return;
    }
    setConfig(credentials);
  };

  const { getRootProps, getInputProps } = useDropzone({
    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    onDrop: useCallback(async (acceptedFiles) => {
      setError("");
      const file = acceptedFiles[0];
      if (!file) {
        return;
      }

      const fileContents = await file.text();
      try {
        parseCredentials(JSON.parse(fileContents));
      } catch (_err) {
        setError("Could not parse credential configuration file.");
      }
    }, []),
    accept: ".json",
  });

  const saEmail = extractSaEmail(config.service_account_impersonation_url);

  return (
    <>
      <FormField
        description={
          <Paragraph>
            Authenticate using your Workload Identity Pool instead of a service
            account key.
          </Paragraph>
        }
        error={error}
        label="Upload WIF credential configuration"
        mt={6}
      >
        {config.audience ? (
          <Box display="flex" flexDirection="column" gap={6}>
            <FormField label="Audience (identity pool)">
              <TextInput isReadOnly value={config.audience} />
            </FormField>

            {saEmail && (
              <FormField label="Impersonated service account">
                <Box display="flex" gap={3}>
                  <TextInput isReadOnly value={saEmail} />
                  <ClipboardButton text={saEmail} />
                </Box>
              </FormField>
            )}

            <FormField label="Subject token type">
              <TextInput isReadOnly value={config.subject_token_type} />
            </FormField>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={4}>
            <Paragraph>
              Follow the setup guide to generate your credential configuration
              file, then upload it below. Workload Identity Federation is
              available by request.
            </Paragraph>

            <DocsLink
              href="/security/gcp/workload-identity-federation"
              label="Workload Identity Federation setup guide"
            />

            <Box
              bg="base.lightBackground"
              borderWidth="hairline"
              borderColor="base.border"
              borderRadius="md"
              borderStyle="dashed"
            >
              <Box
                cursor="pointer"
                py={10}
                textAlign="center"
                {...getRootProps()}
              >
                <input {...getInputProps()} />
                <Paragraph>
                  Drag your file here, or click to select a file.
                </Paragraph>
              </Box>
            </Box>
          </Box>
        )}
      </FormField>
    </>
  );
};
